// SettingsModalShell — the ONE shared shell for every admin modal & drawer.
//
// Option-3 consolidation. Six view files (Publications, Amplifiers, Workspaces,
// PublishTargets, ApiKeys, Team) each defined their own near-identical
// `ModalShell` / `DrawerShell`, and SocialCalendarView hand-rolled a wide drawer
// inline. That duplication is exactly how the chrome drifts over time. This file
// replaces all of them with a single source of truth that renders the canonical
// modal anatomy (paired with settings-modal-reconcile.css).
//
// Drop this at: apps/web/src/components/SettingsModalShell.tsx
// Then in each view: delete the local ModalShell/DrawerShell/ModalShellProps and
//   import { ModalShell, DrawerShell } from "@/components/SettingsModalShell";
// See MIGRATION.md for the per-file steps and the eyebrow string for each modal.
//
// Props are a SUPERSET of the old shells (same `title` / `onClose` / `children`),
// so the import swap alone is behavior-preserving — `eyebrow` and `wide` are
// additive and optional.

import { useEffect } from "react";
import type { ReactNode } from "react";

interface ShellProps {
  title: string;
  /** Uppercase category label rendered above the title (canonical anatomy). */
  eyebrow?: string;
  onClose: () => void;
  children: ReactNode;
}

interface DrawerProps extends ShellProps {
  /** Wider drawer for editor-style content (e.g. social post). */
  wide?: boolean;
}

function useEscToClose(onClose: () => void) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
}

function ShellHeader({
  title,
  eyebrow,
  onClose,
}: Pick<ShellProps, "title" | "eyebrow" | "onClose">) {
  return (
    <header className="settings-modal-header">
      <div>
        {eyebrow ? <span className="settings-modal-eyebrow">{eyebrow}</span> : null}
        <h2>{title}</h2>
      </div>
      <button
        type="button"
        className="settings-modal-close"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>
    </header>
  );
}

export function ModalShell({ title, eyebrow, onClose, children }: ShellProps) {
  useEscToClose(onClose);
  return (
    <div
      className="settings-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <ShellHeader title={title} eyebrow={eyebrow} onClose={onClose} />
        <div className="settings-modal-body">{children}</div>
      </div>
    </div>
  );
}

export function DrawerShell({
  title,
  eyebrow,
  wide,
  onClose,
  children,
}: DrawerProps) {
  useEscToClose(onClose);
  return (
    <div className="settings-drawer-backdrop" onClick={onClose}>
      <aside
        className={`settings-drawer${wide ? " settings-drawer-wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <ShellHeader title={title} eyebrow={eyebrow} onClose={onClose} />
        <div className="settings-modal-body">{children}</div>
      </aside>
    </div>
  );
}
